/**
 * The business details registry (D10 of docs/plans/report-templates.md): every
 * typed, keyed thing a business is, with its kind, whether it holds more than
 * one value, whether it is sensitive, and where it lives.
 *
 * A detail is data, not a fact. A fact (`app.business_fact`) is a sentence
 * every member's prompt reads; a detail is a value a document prints: a GSTIN,
 * a bank account, an FSSAI number. They live in `app.business_detail` (D9),
 * except the three whose home is the tenant row (`name`, `address`, `logo`),
 * which are read from and written to `app.tenant` and never get a row (D11).
 *
 * Every write goes through `validateDetail`, on the server and on the card
 * before it posts. SQL checks only the key and slot shapes and the size.
 *
 * Custom details are `x.<slug>` keys. Their value carries its own label and
 * text, and the label may not be a registry key's label: a custom "GSTIN" would
 * be a second GSTIN nothing checks.
 */
import type { CoversAll } from './covers-all';
import { gstStateCodeOf } from './gst-states';
import { gstinProblem, PAN_SHAPE, parseGstin, tidyGstin } from './gstin';
import type { AppKey } from './i18n/en';
import { enFlat } from './i18n/en';
import { canonicalPhone } from './phone';
import type { BusinessFactAbout, SetupDetailCandidate } from './setup-sources';
import { normaliseWebsiteInput } from './websites';

export type BusinessDetailKind =
  | 'text'
  | 'long_text'
  | 'phone'
  | 'email'
  | 'url'
  | 'tax_id'
  | 'state'
  | 'choice'
  | 'image'
  | 'timezone'
  | 'bank_account'
  /** An `x.<slug>` key: `{ label, text }`. */
  | 'custom';

/** Where a card draws a key. */
export type BusinessDetailGroup = 'identity' | 'tax' | 'bank' | 'registrations' | 'print' | 'custom';

export const BUSINESS_DETAIL_KEYS = [
  // Identity
  'name',
  'legal_name',
  'address',
  'logo',
  'state',
  'phone',
  'email',
  'website',
  // Tax
  'gstin',
  'pan',
  'tax_regime',
  'gst.einvoice',
  'gst.turnover_band',
  // Payment and print
  'bank_account',
  'signature',
  'terms',
  'timezone',
  // Registrations
  'fssai',
  'udyam',
  'cin',
  'iec',
  'drug_licence',
  'trn',
  'school.affiliation_no',
  'school.udise',
] as const;

export type BusinessDetailKey = (typeof BUSINESS_DETAIL_KEYS)[number];

/** A registry key, or a custom `x.<slug>` one. */
export type DetailKey = BusinessDetailKey | `x.${string}`;

/** Where a row came from. `screen` is typed on a card; `setup` was kept at Build. */
export const BUSINESS_DETAIL_SOURCES = ['setup', 'chat', 'screen', 'file', 'website'] as const;
export type BusinessDetailSource = (typeof BUSINESS_DETAIL_SOURCES)[number];

export const DETAIL_LIMITS = {
  /** `business_detail_value_size` in 0084: the value's JSON, in bytes. */
  valueBytes: 4096,
  /** A single-line text. */
  text: 200,
  /** A long text, such as terms. */
  longText: 2000,
  /** A multi-valued key's slot, and a custom key's slug. */
  slot: 32,
  customSlug: 40,
  customLabel: 60,
  customText: 500,
  /** One bank account's text parts. */
  bankText: 100,
  /** An image's address. */
  image: 2048,
  /** The values one multi-valued key may hold. */
  multi: 10,
  /** The rows one write may set. */
  perWrite: 40,
  /** `setup_session.details` (D14). */
  setupRows: 40,
  setupBytes: 32768,
} as const;

/** The parts of a bank account (D10). It may hold only a UPI id. */
export type BankAccount = {
  readonly label: string | null;
  readonly accountName: string | null;
  readonly accountNumber: string | null;
  readonly ifsc: string | null;
  readonly bankName: string | null;
  readonly branch: string | null;
  readonly upiId: string | null;
  readonly isDefault: boolean;
};

/** A bank account as a card or a model sends it: any part may be missing. */
export type BankAccountWire = { readonly [K in keyof BankAccount]?: BankAccount[K] | null };

/** A custom detail's value. */
export type CustomDetailValue = { readonly label: string; readonly text: string };

export type BusinessDetailValue = string | BankAccount | CustomDetailValue;

/** One stored detail as the details route returns it. */
export type BusinessDetail = {
  readonly id: string;
  readonly key: string;
  /** `''` for a single-valued key. */
  readonly slot: string;
  readonly value: BusinessDetailValue;
  readonly source: BusinessDetailSource;
  readonly sourceFileId: string | null;
  readonly createdAt: string;
  readonly createdBy: string | null;
};

type DetailSpec = {
  readonly kind: BusinessDetailKind;
  readonly multi: boolean;
  /** A sensitive part shows its last four characters to anyone who may not change the business. */
  readonly sensitive: boolean;
  readonly home: 'store' | 'tenant';
  readonly group: BusinessDetailGroup;
  readonly labelKey: AppKey;
  /** A choice's values, each with a label key. */
  readonly choices?: readonly { readonly value: string; readonly labelKey: AppKey }[];
  /** A text's own ceiling, under the kind's. */
  readonly max?: number;
  /** A `tax_id`'s tidy form, and why it is not one. */
  readonly tidy?: (text: string) => string;
  readonly check?: (tidy: string) => DetailProblem | null;
};

export type DetailProblem =
  | 'unknown_key'
  | 'wrong_shape'
  | 'empty'
  | 'too_long'
  | 'too_big'
  | 'invalid'
  | 'gstin_shape'
  | 'gstin_state'
  | 'gstin_check'
  | 'unknown_state'
  | 'not_a_choice'
  | 'account_or_upi'
  | 'bad_account'
  | 'bad_ifsc'
  | 'bad_upi'
  | 'label_taken'
  | 'bad_slot';

const upperNoSpace = (text: string): string => text.toUpperCase().replace(/\s+/g, '');
const digitsOnly = (text: string): string => text.replace(/[\s-]/g, '');
const shaped =
  (shape: RegExp) =>
  (tidy: string): DetailProblem | null =>
    shape.test(tidy) ? null : 'invalid';

const SPECS: { readonly [K in BusinessDetailKey]: DetailSpec } = {
  name: { kind: 'text', multi: false, sensitive: false, home: 'tenant', group: 'identity', labelKey: 'business_details.key.name', max: 120 },
  legal_name: { kind: 'text', multi: false, sensitive: false, home: 'store', group: 'identity', labelKey: 'business_details.key.legal_name' },
  address: { kind: 'long_text', multi: false, sensitive: false, home: 'tenant', group: 'identity', labelKey: 'business_details.key.address', max: 500 },
  logo: { kind: 'image', multi: false, sensitive: false, home: 'tenant', group: 'identity', labelKey: 'business_details.key.logo' },
  state: { kind: 'state', multi: false, sensitive: false, home: 'store', group: 'identity', labelKey: 'business_details.key.state' },
  phone: { kind: 'phone', multi: true, sensitive: false, home: 'store', group: 'identity', labelKey: 'business_details.key.phone' },
  email: { kind: 'email', multi: false, sensitive: false, home: 'store', group: 'identity', labelKey: 'business_details.key.email' },
  website: { kind: 'url', multi: false, sensitive: false, home: 'store', group: 'identity', labelKey: 'business_details.key.website' },
  gstin: {
    kind: 'tax_id',
    multi: false,
    sensitive: false,
    home: 'store',
    group: 'tax',
    labelKey: 'business_details.key.gstin',
    tidy: tidyGstin,
    check: (tidy) => {
      const problem = gstinProblem(tidy);
      return problem === null ? null : problem === 'shape' ? 'gstin_shape' : problem === 'state' ? 'gstin_state' : 'gstin_check';
    },
  },
  pan: { kind: 'tax_id', multi: false, sensitive: false, home: 'store', group: 'tax', labelKey: 'business_details.key.pan', tidy: upperNoSpace, check: shaped(PAN_SHAPE) },
  tax_regime: {
    kind: 'choice',
    multi: false,
    sensitive: false,
    home: 'store',
    group: 'tax',
    labelKey: 'business_details.key.tax_regime',
    choices: [
      { value: 'none', labelKey: 'business_details.choice.tax_regime.none' },
      { value: 'gst_regular', labelKey: 'business_details.choice.tax_regime.gst_regular' },
      { value: 'gst_composition', labelKey: 'business_details.choice.tax_regime.gst_composition' },
      { value: 'vat', labelKey: 'business_details.choice.tax_regime.vat' },
    ],
  },
  'gst.einvoice': {
    kind: 'choice',
    multi: false,
    sensitive: false,
    home: 'store',
    group: 'tax',
    labelKey: 'business_details.key.gst_einvoice',
    choices: [
      { value: 'no', labelKey: 'business_details.choice.gst_einvoice.no' },
      { value: 'yes', labelKey: 'business_details.choice.gst_einvoice.yes' },
      { value: 'exempt', labelKey: 'business_details.choice.gst_einvoice.exempt' },
    ],
  },
  'gst.turnover_band': {
    kind: 'choice',
    multi: false,
    sensitive: false,
    home: 'store',
    group: 'tax',
    labelKey: 'business_details.key.gst_turnover_band',
    choices: [
      { value: 'upto_5cr', labelKey: 'business_details.choice.gst_turnover_band.upto_5cr' },
      { value: 'above_5cr', labelKey: 'business_details.choice.gst_turnover_band.above_5cr' },
    ],
  },
  bank_account: { kind: 'bank_account', multi: true, sensitive: true, home: 'store', group: 'bank', labelKey: 'business_details.key.bank_account' },
  signature: { kind: 'image', multi: false, sensitive: false, home: 'store', group: 'print', labelKey: 'business_details.key.signature' },
  terms: { kind: 'long_text', multi: false, sensitive: false, home: 'store', group: 'print', labelKey: 'business_details.key.terms' },
  timezone: { kind: 'timezone', multi: false, sensitive: false, home: 'store', group: 'print', labelKey: 'business_details.key.timezone' },
  fssai: {
    kind: 'tax_id',
    multi: false,
    sensitive: false,
    home: 'store',
    group: 'registrations',
    labelKey: 'business_details.key.fssai',
    tidy: digitsOnly,
    check: shaped(/^\d{14}$/),
  },
  udyam: {
    kind: 'tax_id',
    multi: false,
    sensitive: false,
    home: 'store',
    group: 'registrations',
    labelKey: 'business_details.key.udyam',
    tidy: upperNoSpace,
    check: shaped(/^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/),
  },
  cin: {
    kind: 'tax_id',
    multi: false,
    sensitive: false,
    home: 'store',
    group: 'registrations',
    labelKey: 'business_details.key.cin',
    tidy: upperNoSpace,
    check: shaped(/^[LU]\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/),
  },
  iec: { kind: 'tax_id', multi: false, sensitive: false, home: 'store', group: 'registrations', labelKey: 'business_details.key.iec', tidy: upperNoSpace, check: shaped(/^[A-Z0-9]{10}$/) },
  // A drug licence's number differs by state ("20B/KA/1234", "MH-MZ1-123456"), so only its shape is held.
  drug_licence: {
    kind: 'tax_id',
    multi: false,
    sensitive: false,
    home: 'store',
    group: 'registrations',
    labelKey: 'business_details.key.drug_licence',
    tidy: (text) => text.trim().toUpperCase().replace(/\s+/g, ' '),
    check: shaped(/^(?=.*\d)[A-Z0-9][A-Z0-9 /().,-]{2,59}$/),
  },
  trn: { kind: 'tax_id', multi: false, sensitive: false, home: 'store', group: 'registrations', labelKey: 'business_details.key.trn', tidy: digitsOnly, check: shaped(/^\d{15}$/) },
  'school.affiliation_no': {
    kind: 'tax_id',
    multi: false,
    sensitive: false,
    home: 'store',
    group: 'registrations',
    labelKey: 'business_details.key.school_affiliation_no',
    tidy: upperNoSpace,
    check: shaped(/^(?=.*\d)[A-Z0-9/-]{3,30}$/),
  },
  'school.udise': {
    kind: 'tax_id',
    multi: false,
    sensitive: false,
    home: 'store',
    group: 'registrations',
    labelKey: 'business_details.key.school_udise',
    tidy: digitsOnly,
    check: shaped(/^\d{11}$/),
  },
};

const REGISTRY_KEYS: ReadonlySet<string> = new Set(BUSINESS_DETAIL_KEYS);

export const isBusinessDetailKey = (key: string): key is BusinessDetailKey => REGISTRY_KEYS.has(key);

const CUSTOM_KEY = new RegExp(`^x\\.[a-z0-9][a-z0-9_]{0,${DETAIL_LIMITS.customSlug - 1}}$`);

/** An `x.<slug>` key, its slug lower_snake. */
export const isCustomDetailKey = (key: string): key is `x.${string}` => CUSTOM_KEY.test(key);

export const isDetailKey = (key: string): key is DetailKey => isBusinessDetailKey(key) || isCustomDetailKey(key);

const CUSTOM_SPEC: DetailSpec = { kind: 'custom', multi: false, sensitive: false, home: 'store', group: 'custom', labelKey: 'business_details.key.custom' };

/** What the registry says of a key, or null for a key it does not know. */
export const detailSpecOf = (key: string): DetailSpec | null => (isBusinessDetailKey(key) ? SPECS[key] : isCustomDetailKey(key) ? CUSTOM_SPEC : null);

/** The keys whose home is `app.tenant` (D11). They never get a row. */
export const TENANT_DETAIL_KEYS = BUSINESS_DETAIL_KEYS.filter((key) => SPECS[key].home === 'tenant');

export const isTenantDetailKey = (key: string): boolean => isBusinessDetailKey(key) && SPECS[key].home === 'tenant';

/** The registry keys a card draws in one group, in registry order. */
export const detailKeysOfGroup = (group: BusinessDetailGroup): readonly BusinessDetailKey[] => BUSINESS_DETAIL_KEYS.filter((key) => SPECS[key].group === group);

const SLOT = new RegExp(`^[a-z0-9][a-z0-9_-]{0,${DETAIL_LIMITS.slot - 1}}$`);

/** A single-valued key takes `''`; a multi-valued one a short slug. */
export const detailSlotProblem = (key: string, slot: string): DetailProblem | null => {
  const spec = detailSpecOf(key);
  if (spec === null) return 'unknown_key';
  if (!spec.multi) return slot === '' ? null : 'bad_slot';
  return SLOT.test(slot) ? null : 'bad_slot';
};

/** The first free slot of a multi-valued key: `1`, `2` … */
export const nextDetailSlot = (taken: Iterable<string>): string => {
  const held = new Set(taken);
  let n = 1;
  while (held.has(String(n))) n += 1;
  return String(n);
};

/** UTF-8 bytes of a value's JSON, without TextEncoder (which not every phone runtime has had). */
const jsonBytes = (value: unknown): number => {
  const text = JSON.stringify(value);
  let bytes = 0;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4;
      i += 1;
    } else bytes += 3;
  }
  return bytes;
};

export type DetailCheck = { readonly ok: true; readonly value: BusinessDetailValue } | { readonly ok: false; readonly problem: DetailProblem };

const refused = (problem: DetailProblem): DetailCheck => ({ ok: false, problem });

const oneLine = (text: string): string => text.replace(/\s+/g, ' ').trim();

const longText = (text: string): string =>
  text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trimEnd())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const IFSC = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const UPI = /^[a-z0-9][a-z0-9._-]{1,255}@[a-z][a-z0-9]{1,63}$/;
const ACCOUNT = /^[A-Z0-9]{6,34}$/;
const IMAGE = /^(https:\/\/.+|\/v1\/businesses\/[^\s]+)$/;

const textOf = (raw: unknown): string | null => (typeof raw === 'string' ? raw : null);

const checkScalar = (spec: DetailSpec, raw: unknown): DetailCheck => {
  const given = textOf(raw);
  if (given === null) return refused('wrong_shape');
  switch (spec.kind) {
    case 'text': {
      const text = oneLine(given);
      if (text === '') return refused('empty');
      return text.length > (spec.max ?? DETAIL_LIMITS.text) ? refused('too_long') : { ok: true, value: text };
    }
    case 'long_text': {
      const text = longText(given);
      if (text === '') return refused('empty');
      return text.length > (spec.max ?? DETAIL_LIMITS.longText) ? refused('too_long') : { ok: true, value: text };
    }
    case 'phone': {
      const trimmed = given.trim();
      if (trimmed === '') return refused('empty');
      const canonical = canonicalPhone(trimmed);
      const tidy = canonical.startsWith('+') ? canonical : canonical.replace(/[\s().-]/g, '');
      return /^\+?\d{6,15}$/.test(tidy) ? { ok: true, value: tidy } : refused('invalid');
    }
    case 'email': {
      const email = given.trim().toLowerCase();
      if (email === '') return refused('empty');
      if (email.length > 254) return refused('too_long');
      return EMAIL.test(email) ? { ok: true, value: email } : refused('invalid');
    }
    case 'url': {
      if (given.trim() === '') return refused('empty');
      const read = normaliseWebsiteInput(given);
      if (read.kind !== 'url') return refused('invalid');
      return read.url.length > DETAIL_LIMITS.image ? refused('too_long') : { ok: true, value: read.url };
    }
    case 'tax_id': {
      const tidy = (spec.tidy ?? upperNoSpace)(given);
      if (tidy === '') return refused('empty');
      const problem = spec.check?.(tidy) ?? null;
      return problem === null ? { ok: true, value: tidy } : refused(problem);
    }
    case 'state': {
      if (given.trim() === '') return refused('empty');
      const code = gstStateCodeOf(given);
      return code === null ? refused('unknown_state') : { ok: true, value: code };
    }
    case 'choice': {
      const value = given.trim().toLowerCase();
      if (value === '') return refused('empty');
      return spec.choices?.some((one) => one.value === value) ? { ok: true, value } : refused('not_a_choice');
    }
    case 'image': {
      const value = given.trim();
      if (value === '') return refused('empty');
      if (value.length > DETAIL_LIMITS.image) return refused('too_long');
      return IMAGE.test(value) && !value.includes('..') ? { ok: true, value } : refused('invalid');
    }
    case 'timezone': {
      const value = given.trim();
      if (value === '') return refused('empty');
      try {
        const zone = new Intl.DateTimeFormat('en', { timeZone: value }).resolvedOptions().timeZone;
        return { ok: true, value: zone ?? value };
      } catch {
        return refused('invalid');
      }
    }
    default:
      return refused('wrong_shape');
  }
};

const bankPart = (raw: unknown): string | null | 'bad' => {
  if (raw === undefined || raw === null) return null;
  if (typeof raw !== 'string') return 'bad';
  const text = oneLine(raw);
  return text === '' ? null : text;
};

const checkBankAccount = (raw: unknown): DetailCheck => {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return refused('wrong_shape');
  const given = raw as Record<string, unknown>;
  const parts = {
    label: bankPart(given.label),
    accountName: bankPart(given.accountName),
    accountNumber: bankPart(given.accountNumber),
    ifsc: bankPart(given.ifsc),
    bankName: bankPart(given.bankName),
    branch: bankPart(given.branch),
    upiId: bankPart(given.upiId),
  };
  if (Object.values(parts).some((one) => one === 'bad')) return refused('wrong_shape');
  if (given.isDefault !== undefined && given.isDefault !== null && typeof given.isDefault !== 'boolean') return refused('wrong_shape');
  const plain = parts as { readonly [K in keyof typeof parts]: string | null };
  if ([plain.label, plain.accountName, plain.bankName, plain.branch].some((one) => one !== null && one.length > DETAIL_LIMITS.bankText)) return refused('too_long');
  const accountNumber = plain.accountNumber === null ? null : plain.accountNumber.toUpperCase().replace(/[\s-]/g, '');
  const ifsc = plain.ifsc === null ? null : plain.ifsc.toUpperCase().replace(/\s+/g, '');
  const upiId = plain.upiId === null ? null : plain.upiId.toLowerCase().replace(/\s+/g, '');
  if (accountNumber === null && upiId === null) return refused('account_or_upi');
  if (accountNumber !== null && !ACCOUNT.test(accountNumber)) return refused('bad_account');
  if (ifsc !== null && !IFSC.test(ifsc)) return refused('bad_ifsc');
  if (upiId !== null && !UPI.test(upiId)) return refused('bad_upi');
  const value: BankAccount = {
    label: plain.label,
    accountName: plain.accountName,
    accountNumber,
    ifsc,
    bankName: plain.bankName,
    branch: plain.branch,
    upiId,
    isDefault: given.isDefault === true,
  };
  return { ok: true, value };
};

// Punctuation and spacing only: a Unicode property escape is not something every phone runtime has parsed.
const foldLabel = (text: string): string => text.toLowerCase().replace(/[\s.,:;'’"()/_#-]+/g, ' ').trim();

/** Every registry key's English label, folded, and the key itself: what a custom label may not be. */
const TAKEN_LABELS: ReadonlySet<string> = new Set(
  BUSINESS_DETAIL_KEYS.flatMap((key) => [foldLabel(enFlat[SPECS[key].labelKey] ?? ''), foldLabel(key)]).filter((one) => one !== ''),
);

/** True when a custom detail's label would read as a registry key's. */
export const isTakenDetailLabel = (label: string): boolean => TAKEN_LABELS.has(foldLabel(label));

const checkCustom = (raw: unknown): DetailCheck => {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return refused('wrong_shape');
  const given = raw as Record<string, unknown>;
  if (typeof given.label !== 'string' || typeof given.text !== 'string') return refused('wrong_shape');
  const label = oneLine(given.label);
  const text = longText(given.text);
  if (label === '' || text === '') return refused('empty');
  if (label.length > DETAIL_LIMITS.customLabel || text.length > DETAIL_LIMITS.customText) return refused('too_long');
  if (isTakenDetailLabel(label)) return refused('label_taken');
  return { ok: true, value: { label, text } };
};

/**
 * A value checked against its key, and tidied: a GSTIN upper-cased, a phone in
 * its stored form, a state name made a code, a bank account's parts trimmed.
 */
/**
 * The words for why a value was refused, said as what to do: the web's
 * Workspace details and detail cards, and OS's refusal of a value said in
 * chat, read the same sentence. Takes a write's problem code too
 * (`BusinessDetailWriteProblemCode`); one with no words of its own reads
 * "Check this value and try again."
 */
export const detailProblemKey = (problem: string): AppKey => {
  switch (problem) {
    case 'gstin_check':
      return 'business_details.problem.gstin_check';
    case 'gstin_shape':
    case 'gstin_state':
      return 'business_details.problem.gstin_shape';
    case 'unknown_state':
      return 'business_details.problem.state';
    case 'account_or_upi':
      return 'business_details.problem.account_or_upi';
    case 'bad_ifsc':
      return 'business_details.problem.ifsc';
    case 'bad_upi':
      return 'business_details.problem.upi';
    case 'bad_account':
    case 'masked':
      return 'business_details.problem.account';
    case 'label_taken':
      return 'business_details.problem.label_taken';
    case 'pan_mismatch':
      return 'business_details.problem.pan_mismatch';
    case 'too_long':
    case 'too_big':
      return 'business_details.problem.too_long';
    case 'empty':
      return 'business_details.problem.empty';
    default:
      return 'business_details.problem.invalid';
  }
};

export const validateDetail = (key: string, value: unknown): DetailCheck => {
  const spec = detailSpecOf(key);
  if (spec === null) return refused('unknown_key');
  const checked = spec.kind === 'bank_account' ? checkBankAccount(value) : spec.kind === 'custom' ? checkCustom(value) : checkScalar(spec, value);
  if (checked.ok && jsonBytes(checked.value) > DETAIL_LIMITS.valueBytes) return refused('too_big');
  return checked;
};

/** The tidy value, or null when it is not one. */
export const normaliseDetail = (key: string, value: unknown): BusinessDetailValue | null => {
  const checked = validateDetail(key, value);
  return checked.ok ? checked.value : null;
};

/**
 * The details a value gives without being typed again (D12): a valid GSTIN's
 * state and PAN. Empty for every other key.
 */
export const detailsDerivedFrom = (key: string, value: unknown): readonly { readonly key: 'state' | 'pan'; readonly value: string }[] => {
  if (key !== 'gstin' || typeof value !== 'string') return [];
  const parsed = parseGstin(value);
  return parsed === null
    ? []
    : [
        { key: 'state', value: parsed.state },
        { key: 'pan', value: parsed.pan },
      ];
};

/**
 * The Build sheet's candidates Build cannot keep beside the others (step 2.3
 * of docs/plans/report-templates.md): of the ones not removed, in the sheet's
 * order with a GSTIN first, a second value of a single-valued key (a second
 * GSTIN), a state or PAN other than the kept GSTIN's (D12), and a multi-valued
 * key's values past its cap. The server writes by it and the sheet marks them,
 * so what the person sees is what is kept. An unknown key is left alone.
 */
export const setupDetailsOutranked = (sheet: readonly SetupDetailCandidate[], removed: ReadonlySet<string>): ReadonlySet<string> => {
  const left = sheet.filter((one) => !removed.has(one.id));
  const single = new Map<string, string>();
  const multi = new Map<string, number>();
  const out = new Set<string>();
  for (const one of [...left.filter((each) => each.key === 'gstin'), ...left.filter((each) => each.key !== 'gstin')]) {
    const spec = detailSpecOf(one.key);
    if (spec === null) continue;
    if (spec.multi) {
      const count = multi.get(one.key) ?? 0;
      if (count >= DETAIL_LIMITS.multi) out.add(one.id);
      else multi.set(one.key, count + 1);
      continue;
    }
    const held = single.get(one.key);
    if (held !== undefined) {
      if (held !== one.value) out.add(one.id);
      continue;
    }
    single.set(one.key, one.value);
    for (const derived of detailsDerivedFrom(one.key, one.value)) if (!single.has(derived.key)) single.set(derived.key, derived.value);
  }
  return out;
};

/** The kinds of detail a sentence may hold that belong in the details store, not in a fact (D21). */
export type DetailInText = 'gstin' | 'pan' | 'ifsc' | 'upi' | 'email';

const GSTIN_IN_TEXT = /(?<![A-Za-z0-9])\d{2}[A-Za-z]{5}\d{4}[A-Za-z][1-9A-Za-z][Zz][0-9A-Za-z](?![A-Za-z0-9])/;
const PAN_IN_TEXT = /(?<![A-Za-z0-9])[A-Za-z]{5}\d{4}[A-Za-z](?![A-Za-z0-9])/;
const IFSC_IN_TEXT = /(?<![A-Za-z0-9])[A-Za-z]{4}0[A-Za-z0-9]{6}(?![A-Za-z0-9])/;
const EMAIL_IN_TEXT = /[^\s@<>()]+@[^\s@<>()]+\.[A-Za-z]{2,}/;
const UPI_IN_TEXT = /(?<![^\s(])[A-Za-z0-9._-]{2,}@[A-Za-z][A-Za-z0-9]+(?![A-Za-z0-9.@])/;

/**
 * The first business detail a sentence holds, by shape: a GSTIN, a PAN, an
 * IFSC, a UPI id or an email. A sentence holding one is a detail, which the
 * fact store must not keep a second copy of (D21 of docs/plans/report-templates.md).
 */
export const detailInText = (text: string): DetailInText | null => {
  if (GSTIN_IN_TEXT.test(text)) return 'gstin';
  if (PAN_IN_TEXT.test(text)) return 'pan';
  if (IFSC_IN_TEXT.test(text)) return 'ifsc';
  if (EMAIL_IN_TEXT.test(text)) return 'email';
  if (UPI_IN_TEXT.test(text)) return 'upi';
  return null;
};

/** A sensitive part as someone who may not change the business sees it: its last four characters. */
export const maskTail = (text: string): string => (text.length <= 4 ? text : `••••${text.slice(-4)}`);

/** True when the text is a masked tail, so a card never posts one back as a value. */
export const isMaskedTail = (text: string): boolean => text.startsWith('••••');

/** A detail's value with its sensitive parts masked, for a reader who may not change the business (D13). */
export const maskDetailValue = (key: string, value: BusinessDetailValue): BusinessDetailValue => {
  const spec = detailSpecOf(key);
  if (spec === null || !spec.sensitive) return value;
  if (spec.kind === 'bank_account' && typeof value === 'object' && 'isDefault' in value) {
    return { ...value, accountNumber: value.accountNumber === null ? null : maskTail(value.accountNumber) };
  }
  return typeof value === 'string' ? maskTail(value) : value;
};

/** A custom key made from a label: `FSSAI no.` becomes `x.fssai_no`. Null when nothing of the label is Latin. */
export const customDetailKeyOf = (label: string, taken: Iterable<string> = []): `x.${string}` | null => {
  const base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, DETAIL_LIMITS.customSlug - 3)
    .replace(/_+$/g, '');
  if (base === '') return null;
  const held = new Set(taken);
  let key: `x.${string}` = `x.${base}`;
  for (let n = 2; held.has(key); n += 1) key = `x.${base}_${n}`;
  return key;
};

/**
 * The key a new custom detail is saved under: one made from its label, or, for
 * a label with nothing Latin in it, the first `x.detail_<n>` the business does
 * not hold. Never a key in `taken`, so a new detail never corrects another.
 */
export const newCustomDetailKey = (label: string, taken: Iterable<string>): `x.${string}` => {
  const held = new Set(taken);
  const fromLabel = customDetailKeyOf(label, held);
  if (fromLabel !== null) return fromLabel;
  let n = 1;
  while (held.has(`x.detail_${n}`)) n += 1;
  return `x.detail_${n}`;
};

/**
 * The closed set of what a business fact is about (`BusinessFactAbout`), once.
 * The findings merge and the setup gap reader both check against it.
 */
export const BUSINESS_FACT_ABOUTS = ['name', 'phone', 'email', 'address', 'area', 'hours', 'gstin', 'category', 'delivery', 'licence', 'other'] as const satisfies readonly BusinessFactAbout[];
true satisfies CoversAll<BusinessFactAbout, typeof BUSINESS_FACT_ABOUTS>;

const FACT_ABOUTS: ReadonlySet<string> = new Set(BUSINESS_FACT_ABOUTS);

export const isBusinessFactAbout = (about: string): about is BusinessFactAbout => FACT_ABOUTS.has(about);

const DETAIL_OF_ABOUT: { readonly [K in BusinessFactAbout]?: BusinessDetailKey } = {
  name: 'name',
  phone: 'phone',
  email: 'email',
  address: 'address',
  gstin: 'gstin',
};

/** The detail key a fact's `about` names, or null for one that stays a fact (area, hours, category …). */
export const detailKeyOfAbout = (about: BusinessFactAbout): BusinessDetailKey | null => DETAIL_OF_ABOUT[about] ?? null;
